import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { auth, user as userApi, linkedin, ai as aiApi, calendar as calendarApi, knowledge as knowledgeApi } from '../../lib/api';
import { formatApiDateTime, formatRelative } from '../../lib/format';
import EngagementVoiceSettings from './settings/EngagementVoiceSettings';
import {
  User,
  Key,
  Link2,
  Sparkles,
  Save,
  Loader2,
  Check,
  Trash2,
  ExternalLink,
  AlertCircle,
  Clock,
  ShieldCheck,
  CalendarDays,
  Lock,
  Database,
  RefreshCcw,
  Pencil,
} from 'lucide-react';

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
];

const VALID_TONES = new Set(TONES.map((tone) => tone.value));
const REPLY_PERSONAS = [
  { value: 'woman', label: 'Woman' },
  { value: 'man', label: 'Man' },
  { value: 'brand', label: 'Brand / team' },
];
const VALID_REPLY_PERSONAS = new Set(REPLY_PERSONAS.map((persona) => persona.value));

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MASKED_KEY = '\u2022'.repeat(12);
const isMaskedKey = (value = '') => value.includes('\u2022') || value.includes('\u00e2');
const clampQuietWindow = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 5;
  return Math.min(30, Math.max(5, parsed));
};
const clampRangeMax = (value, minimum = 5) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return Math.max(30, minimum);
  return Math.min(30, Math.max(minimum, parsed));
};
const normalizeAiSettings = (settings = {}) => ({
  ...settings,
  tone: VALID_TONES.has(settings.tone) ? settings.tone : 'professional',
  reply_persona: VALID_REPLY_PERSONAS.has(settings.reply_persona) ? settings.reply_persona : 'brand',
  response_delay_minutes: clampQuietWindow(settings.response_delay_minutes ?? 5),
  variable_delay: !!settings.variable_delay,
  variable_delay_min: clampQuietWindow(settings.variable_delay_min ?? settings.response_delay_minutes ?? 5),
  variable_delay_max: clampRangeMax(settings.variable_delay_max ?? 30, clampQuietWindow(settings.variable_delay_min ?? settings.response_delay_minutes ?? 5)),
  low_confidence_action: ['fallback', 'approval', 'skip'].includes(settings.low_confidence_action)
    ? settings.low_confidence_action
    : 'fallback',
});
const AI_DIAGNOSTIC_STATUS_LABELS = {
  waiting_for_next_poll: 'Waiting for next auto-poll',
  waiting_for_quiet_window: 'Waiting for quiet window',
  blocked_by_settings: 'Blocked by settings',
  blocked_by_missing_key: 'Missing OpenAI key',
  blocked_by_working_hours: 'Outside working hours',
  blocked_by_chat_disable: 'AI reply disabled on chat',
};
const AI_DIAGNOSTIC_REASON_LABELS = {
  ai_disabled: 'AI Auto-Reply is currently disabled.',
  all_message_triggers_disabled: 'All message triggers are disabled.',
  no_connected_account: 'No connected LinkedIn account is available.',
  missing_openai_key: 'No per-user or fallback OpenAI key is configured.',
  missing_knowledge: 'No ready knowledge base is configured for this client yet.',
  knowledge_indexing_error: 'Knowledge indexing is pending or failed. Reindex the source and try again.',
  no_relevant_knowledge: 'No relevant knowledge was found for the latest conversation burst.',
  outside_working_hours: 'The current time is outside configured working hours.',
  chat_ai_disabled: 'The newest chat waiting for a reply has AI turned off.',
  pending_job: 'A pending AI reply job is already waiting for its quiet window.',
  quiet_window_pending: 'A new reply is scheduled and still inside its quiet window.',
  low_confidence: 'The model confidence was below your threshold.',
  trigger_reply_disabled: 'Reply trigger is disabled.',
  trigger_first_message_disabled: 'First-message trigger is disabled.',
  conversation_not_found: 'The source conversation no longer exists.',
  no_messages: 'No recent messages were available for reply generation.',
  latest_message_not_actionable: 'The latest message no longer needs a reply.',
  send_failed: 'Sending the generated reply failed.',
  job_exception: 'The AI job hit an unexpected processing error.',
};

function humanizeAiReason(reason) {
  if (!reason) return 'No issues detected.';
  return AI_DIAGNOSTIC_REASON_LABELS[reason] || reason.replace(/_/g, ' ');
}

function formatDiagnosticMoment(value) {
  if (!value) return 'Not yet';
  const exact = formatApiDateTime(value, { dateStyle: 'medium', timeStyle: 'short' });
  const relative = formatRelative(value);
  if (exact && relative && relative !== 'â€”') {
    return `${exact} (${relative} ago)`;
  }
  return exact || relative || 'Not yet';
}

function formatKnowledgeMoment(value) {
  if (!value) return 'Not indexed yet';
  return formatApiDateTime(value, { dateStyle: 'medium', timeStyle: 'short' }) || 'Not indexed yet';
}

const KNOWLEDGE_STATUS_LABELS = {
  pending: 'Pending',
  indexing: 'Indexing',
  ready: 'Ready',
  error: 'Error',
};
const KB_RECOMMENDED_CUSTOM_INSTRUCTIONS = 'Be concise and factual. If a request is outside our supported services, clearly say we do not provide it and redirect to the relevant services we do offer. Do not speculate beyond the knowledge base.';
const KB_STARTER_TEMPLATES = [
  {
    key: 'ai-reply-rules',
    title: 'AI Reply Rules',
    language: 'English',
    description: 'Hard guardrails for strict factual replies, hard redirects, and approval-over-speculation.',
    raw_text: `AI REPLY RULES

You are replying on behalf of this business. Your job is to send short, accurate, business-relevant replies only.

CORE RULES
- Use only facts that are explicitly present in the knowledge base and the current conversation.
- Do not guess, invent, assume, or extrapolate beyond the knowledge base.
- If the knowledge base does not support a claim, do not make that claim.
- If a detail is missing, say so briefly and stay within known facts.

SCOPE CONTROL
- If the sender asks about a service, product, material, capability, timeline, price, guarantee, or process that is not explicitly supported by the knowledge base, do not pretend that we offer it.
- Treat a service as supported only when it is clearly evidenced by uploaded text notes, indexed URL pages, or explicit conversation facts.
- If the sender asks about something outside our scope or something that is not confirmed by the knowledge base, clearly say that this is not confirmed on our side and do not present it as an offered service.
- Do not ask follow-up questions about unsupported services.
- Instead, redirect the conversation to the services that are explicitly listed in the knowledge base.

REDIRECT BEHAVIOR
- For unsupported or irrelevant requests, use a hard redirect.
- Preferred pattern:
  1. Briefly state that this is not one of our services.
  2. Mention the relevant services we actually provide.
  3. Offer to provide more information only about those supported services.
- Do not open a long discussion about the unsupported topic.

TRUTHFULNESS
- Never invent prices, discounts, lead times, delivery dates, guarantees, technical specs, references, or availability.
- Never claim experience, specialization, or capability unless it is explicitly stated in the knowledge base.
- If the sender asks for something that is not covered in the knowledge base, respond conservatively.

STYLE
- Keep replies concise, direct, and businesslike.
- Default length: 2 to 4 sentences.
- Do not use fluff, hype, flirt, sarcasm, or exaggerated marketing language.
- Stay calm and professional even if the sender is rude, unclear, or off-topic.

LANGUAGE
- Reply in the same language as the sender whenever possible.
- If the sender writes in Slovak, reply in Slovak.
- If the sender writes in Czech, reply in Czech.
- If the sender writes in English, reply in English.

WHEN INFORMATION IS MISSING
- If the answer is not supported by the knowledge base, do not improvise.
- Either:
  - say that you do not have enough confirmed information, or
  - redirect to supported services if the message is outside scope, or
  - use the fallback / approval path if uncertain cases should be handled by a human.
- If confidence is not high enough, prefer human approval rather than a speculative answer.

EXAMPLE BEHAVIOR
- Bad: "What exactly would you like to know about microcement?"
- Good: "We do not specialize in microcement finishes. We focus on [supported services]. If you have a relevant question about those services, we can provide more information."

FINAL RULE
- Facts come from the knowledge base.
- Tone may change wording, but it must never change the facts.`,
  },
  {
    key: 'services-and-scope',
    title: 'Services and Scope',
    language: 'English',
    description: 'Structured evidence about what the business actually does. Anything not evidenced here or in indexed URLs should stay unconfirmed.',
    raw_text: `COMPANY
- Name: [add company name]
- Who we are: [one short sentence describing what the company does]
- Ideal customer profile: [B2B / B2C / segment]

SUPPORTED SERVICES CONFIRMED BY EVIDENCE
- [Service 1] — [short description]
  Aliases / keywords: [synonyms, phrases real prospects might use]
  Evidence: [URL page / text note / approved internal fact]
- [Service 2] — [short description]
  Aliases / keywords: [synonyms]
  Evidence: [URL page / text note / approved internal fact]
- [Service 3] — [short description]
  Aliases / keywords: [synonyms]
  Evidence: [URL page / text note / approved internal fact]

OPTIONAL EXPLICITLY UNSUPPORTED SERVICES
- [Only add services that should be rejected immediately and explicitly]
- [Example: microcement finishes]
- [Example: electrical installation]

UNCONFIRMED MEANS NOT SUPPORTED
- If a service is not evidenced in the knowledge base, AI must treat it as unconfirmed.
- AI must not infer service availability from broad wording or adjacent topics.
- AI may redirect to confirmed services, but it must not pretend the unconfirmed service is available.

ALLOWED FACTS
- [claim we are allowed to make]
- [claim we are allowed to make]
- [claim we are allowed to make]

DO NOT CLAIM THESE UNLESS THEY ARE EXPLICITLY CONFIRMED HERE
- pricing
- delivery timelines
- discounts
- guarantees
- technical capability claims
- availability of a specific service

SAFE NEXT STEP FOR RELEVANT INQUIRIES
- [how AI should safely move the conversation forward]
- [for example: ask for a short brief, contact details, or a preferred call time]

SAFE HANDOFF FOR UNCERTAIN INQUIRIES
- [fallback wording if the answer is not clearly supported]
- [who should review uncertain or edge-case questions]
- [what AI may promise about follow-up, if anything]`,
  },
  {
    key: 'approved-example-replies',
    title: 'Approved Example Replies',
    language: 'English',
    description: 'Approved reply skeletons for unsupported requests, service overview, pricing, timelines, and unclear questions.',
    raw_text: `APPROVED EXAMPLE REPLIES

Q: Do you provide [out-of-scope service]?
A: Hello, we do not specialize in that service. We focus on [supported services]. If your request is related to those services, we can share more relevant information.

Q: Do you provide [service that is not confirmed anywhere in the knowledge base]?
A: Hello, I do not have enough confirmed information to say that we provide that service. We currently have confirmed information about [supported services]. If your request is related to those services, we can continue there, or a team member can review your question.

Q: What services do you offer?
A: Hello, we focus on [short list of main services]. If helpful, I can briefly point out which of these is most relevant for your type of project.

Q: How much does it cost?
A: Hello, we do not quote exact pricing without more context. If you send a short outline of the project, we can indicate what is relevant and which of our services may fit.

Q: Can you deliver it next week?
A: Hello, we do not confirm delivery timelines before checking scope and capacity. If you send a short brief, we can clarify whether the request fits our services and what the next step should be.

Q: I only need a quick answer.
A: Hello, we are happy to help within the services we provide. If you send a short summary of what you need, we can confirm whether it fits our scope.

Q: [Short or unclear question without context]
A: Hello, to answer accurately we need a little more context. If this is about [supported services], please send one or two sentences about what you need and we will respond more precisely.`,
  },
];

function normalizeKnowledgeTitle(value = '') {
  return value.trim().toLowerCase();
}

function findKnowledgeTemplateByTitle(title = '') {
  return KB_STARTER_TEMPLATES.find((template) => normalizeKnowledgeTitle(template.title) === normalizeKnowledgeTitle(title)) || null;
}

export default function Settings() {
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [openaiKey, setOpenaiKey] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [banner, setBanner] = useState(null);

  const [security, setSecurity] = useState({
    two_factor_enabled: false,
    recovery_codes_remaining: 0,
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorDisable, setTwoFactorDisable] = useState({ current_password: '', code: '' });
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarBusy, setCalendarBusy] = useState(false);

  const [savingOpenAI, setSavingOpenAI] = useState(false);
  const [openaiSaved, setOpenaiSaved] = useState(false);

  // Outreach settings
  const [outreachEnabled, setOutreachEnabled] = useState(false);
  const [outreachTemplate, setOutreachTemplate] = useState('');
  const [outreachDelay, setOutreachDelay] = useState(60);
  const [outreachMaxPerDay, setOutreachMaxPerDay] = useState(50);

  // AI autoresponder settings
  const [ai, setAi] = useState({
    enabled: false,
    tone: 'professional',
    reply_persona: 'brand',
    response_delay_minutes: 5,
    variable_delay: false,
    variable_delay_min: 5,
    variable_delay_max: 30,
    working_hours_enabled: false,
    working_hours_start: '09:00',
    working_hours_end: '18:00',
    working_days: [0, 1, 2, 3, 4],
    trigger_first_message: true,
    trigger_reply: true,
    trigger_invite_accepted: false,
    require_approval: false,
    approval_via_email: false,
    confidence_threshold: 0.6,
    low_confidence_action: 'fallback',
    fallback_message: '',
    custom_instructions: '',
  });
  const [savingAi, setSavingAi] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);
  const [backfillingAi, setBackfillingAi] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [aiDiagnostics, setAiDiagnostics] = useState(null);
  const [loadingAiDiagnostics, setLoadingAiDiagnostics] = useState(false);
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [loadingKnowledge, setLoadingKnowledge] = useState(false);
  const [savingKnowledge, setSavingKnowledge] = useState(false);
  const [knowledgeBusyId, setKnowledgeBusyId] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState({
    id: null,
    type: 'text',
    title: '',
    raw_text: '',
    url: '',
  });
  const [activeKnowledgeTemplate, setActiveKnowledgeTemplate] = useState(null);
  const [knowledgeEditorTarget, setKnowledgeEditorTarget] = useState(null);
  const defaultKnowledgeEditorRef = useRef(null);
  const [expandedKnowledgeSource, setExpandedKnowledgeSource] = useState(null);
  const [knowledgeEditorReady, setKnowledgeEditorReady] = useState(false);

  useEffect(() => {
    setKnowledgeEditorReady(true);
  }, []);

  useEffect(() => {
    if (!location.hash) return undefined;
    const targetId = location.hash.slice(1);
    const scrollToTarget = () => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const initialTimer = window.setTimeout(scrollToTarget, 120);
    const settledTimer = window.setTimeout(scrollToTarget, 900);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearTimeout(settledTimer);
    };
  }, [location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accountId = params.get('account_id');
    if (params.get('connected') !== 'linkedin' || !accountId) return;

    setBanner({ type: 'success', text: 'LinkedIn connected. Finalizing account setup...' });
    linkedin.completeConnection(accountId)
      .then(async () => {
        setBanner({ type: 'success', text: 'LinkedIn connected successfully. Inbox is syncing...' });
        await loadAccounts();
        await loadAiDiagnostics({ silent: true });
      })
      .catch((err) => {
        console.error('Failed to finalize LinkedIn connection:', err);
        setBanner({ type: 'error', text: err.message || 'LinkedIn connected, but account setup failed.' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAccounts();
    loadAiSettings();
    loadAiDiagnostics();
    loadKnowledgeSources();
    loadSecurity();
    loadCalendarStatus();

    // Handle redirect back from Unipile hosted auth
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'linkedin') {
      setBanner({ type: 'success', text: 'LinkedIn connected successfully. Inbox is syncing…' });
      window.history.replaceState({}, '', '/app/settings');
      setTimeout(loadAccounts, 2000);
    } else if (params.get('calendar_connected') === 'google') {
      setBanner({ type: 'success', text: 'Google Calendar connected successfully.' });
      window.history.replaceState({}, '', '/app/settings');
      setTimeout(loadCalendarStatus, 500);
    } else if (params.get('calendar_error')) {
      setBanner({ type: 'error', text: `Google Calendar connection failed: ${params.get('calendar_error')}` });
      window.history.replaceState({}, '', '/app/settings');
    } else if (params.get('error')) {
      setBanner({ type: 'error', text: 'LinkedIn connection failed. Please try again.' });
      window.history.replaceState({}, '', '/app/settings');
    }

    if (user?.settings) {
      setOpenaiKey(user.settings.openai_api_key_configured ? MASKED_KEY : '');
      const outreach = user.settings.auto_outreach || {};
      setOutreachEnabled(outreach.enabled || false);
      setOutreachTemplate(outreach.template || '');
      setOutreachDelay(outreach.delay_minutes || 60);
      setOutreachMaxPerDay(outreach.max_per_day || 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const intervalId = window.setInterval(() => {
      loadAiDiagnostics({ silent: true });
      loadKnowledgeSources({ silent: true });
    }, 30000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const data = await linkedin.getAccounts();
      setAccounts(data.accounts);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadAiSettings = async () => {
    try {
      const { settings } = await aiApi.getSettings();
      setAi((prev) => ({ ...prev, ...normalizeAiSettings(settings) }));
    } catch (err) {
      console.error('Failed to load AI settings:', err);
    }
  };

  const loadAiDiagnostics = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingAiDiagnostics(true);
      const data = await aiApi.diagnostics();
      setAiDiagnostics(data);
    } catch (err) {
      console.error('Failed to load AI diagnostics:', err);
      setAiDiagnostics((prev) => ({
        ...(prev || {}),
        load_error: err.message || 'Could not load AI diagnostics.',
      }));
    } finally {
      if (!silent) setLoadingAiDiagnostics(false);
    }
  };

  const loadKnowledgeSources = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingKnowledge(true);
      const data = await knowledgeApi.listSources();
      setKnowledgeSources(data.sources || []);
    } catch (err) {
      console.error('Failed to load knowledge sources:', err);
      if (!silent) {
        setBanner({ type: 'error', text: err.message || 'Could not load knowledge sources.' });
      }
    } finally {
      if (!silent) setLoadingKnowledge(false);
    }
  };

  const loadSecurity = async () => {
    try {
      const data = await userApi.security();
      setSecurity(data);
    } catch (err) {
      console.error('Failed to load security status:', err);
    }
  };

  const loadCalendarStatus = async () => {
    try {
      const data = await calendarApi.status();
      setCalendarStatus(data);
    } catch (err) {
      console.error('Failed to load calendar status:', err);
      setCalendarStatus({ configured: false, connected: false, error: err.message });
    }
  };

  const handleSaveOpenAI = async () => {
    if (!openaiKey || isMaskedKey(openaiKey)) return;
    try {
      setSavingOpenAI(true);
      await userApi.update({ openai_api_key: openaiKey });
      await checkAuth();
      setOpenaiKey(MASKED_KEY);
      await loadAiDiagnostics({ silent: true });
      setOpenaiSaved(true);
      setTimeout(() => setOpenaiSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save OpenAI key:', err);
    } finally {
      setSavingOpenAI(false);
    }
  };

  const handleClearOpenAI = async () => {
    if (!window.confirm('Remove your OpenAI API key?')) return;
    try {
      await userApi.update({ openai_api_key: '' });
      await checkAuth();
      setOpenaiKey('');
      await loadAiDiagnostics({ silent: true });
    } catch (err) {
      console.error('Failed to clear OpenAI key:', err);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const updates = { name };
      if (openaiKey && isMaskedKey(openaiKey)) {
        await userApi.update(updates);
        await checkAuth();
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        return;
      }
      if (openaiKey) {
        updates.openai_api_key = openaiKey;
      }
      await userApi.update(updates);
      await checkAuth();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutreach = async () => {
    try {
      setLoading(true);
      await userApi.updateOutreachSettings({
        enabled: outreachEnabled,
        template: outreachTemplate,
        delay_minutes: outreachDelay,
        max_per_day: outreachMaxPerDay,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save outreach settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const persistAiSettings = async (nextSettings) => {
    setSavingAi(true);
    const payload = normalizeAiSettings(nextSettings);
    const res = await aiApi.updateSettings(payload);
    const normalized = normalizeAiSettings(res?.settings || payload);
    setAi((prev) => ({ ...prev, ...normalized }));
    await loadAiDiagnostics({ silent: true });
    setAiSaved(true);
    setTimeout(() => setAiSaved(false), 2000);
    return normalized;
  };

  const handleSaveAi = async () => {
    try {
      await persistAiSettings(ai);
    } catch (err) {
      console.error('Failed to save AI settings:', err);
      setBanner({ type: 'error', text: err.message || 'Could not save AI settings.' });
    } finally {
      setSavingAi(false);
    }
  };

  const handleToggleAiEnabled = async (enabled) => {
    const previousAi = ai;
    const nextAi = { ...ai, enabled };
    setAi(nextAi);
    try {
      await persistAiSettings(nextAi);
      setBanner({
        type: 'success',
        text: enabled ? 'AI Auto-Reply is enabled.' : 'AI Auto-Reply is disabled.',
      });
    } catch (err) {
      console.error('Failed to toggle AI Auto-Reply:', err);
      setAi(previousAi);
      setBanner({
        type: 'error',
        text: err.message || `Could not ${enabled ? 'enable' : 'disable'} AI Auto-Reply.`,
      });
    } finally {
      setSavingAi(false);
    }
  };

  const handleBackfillAi = async () => {
    try {
      setBackfillingAi(true);
      setBackfillResult(null);
      const res = await aiApi.backfillAutoReply(20);
      setBackfillResult(res);
      await loadAiDiagnostics({ silent: true });
      setBanner({
        type: 'success',
        text: `AI checked ${res.checked || 0} unresolved chats, queued ${res.queued || 0}, sent ${res.sent || 0}.`,
      });
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not generate existing reply drafts.' });
    } finally {
      setBackfillingAi(false);
    }
  };

  const applyRecommendedKbDefaults = () => {
    setAi((prev) => ({
      ...prev,
      confidence_threshold: 0.8,
      low_confidence_action: 'approval',
      custom_instructions: KB_RECOMMENDED_CUSTOM_INSTRUCTIONS,
    }));
    setBanner({
      type: 'success',
      text: 'Recommended KB-safe AI defaults were applied to the form. Save AI settings to persist them.',
    });
  };

  const resetKnowledgeForm = () => {
    setActiveKnowledgeTemplate(null);
    setKnowledgeEditorTarget(null);
    setKnowledgeForm({
      id: null,
      type: 'text',
      title: '',
      raw_text: '',
      url: '',
    });
  };

  const handleInsertKnowledgeTemplate = (template, target) => {
    const hasSimilarSource = knowledgeSources.some(
      (source) => normalizeKnowledgeTitle(source.title) === normalizeKnowledgeTitle(template.title),
    );
    setKnowledgeForm({
      id: null,
      type: 'text',
      title: template.title,
      raw_text: template.raw_text,
      url: '',
    });
    setActiveKnowledgeTemplate(template.key);
    setKnowledgeEditorTarget(target);
    setBanner({
      type: 'success',
      text: hasSimilarSource
        ? `Starter template loaded into the editor. A source with a similar role already exists, so review before saving.`
        : 'Starter template loaded into the editor. Review it and save when ready.',
    });
  };

  const handleSaveKnowledgeSource = async () => {
    const payload = {
      type: knowledgeForm.type,
      title: knowledgeForm.title.trim(),
      raw_text: knowledgeForm.type === 'text' ? knowledgeForm.raw_text : undefined,
      url: knowledgeForm.type === 'url' ? knowledgeForm.url.trim() : undefined,
    };
    try {
      setSavingKnowledge(true);
      if (knowledgeForm.id) {
        await knowledgeApi.updateSource(knowledgeForm.id, payload);
        setBanner({ type: 'success', text: 'Knowledge source updated. Reindexing started.' });
      } else {
        await knowledgeApi.createSource(payload);
        setBanner({ type: 'success', text: 'Knowledge source added. Indexing started.' });
      }
      resetKnowledgeForm();
      await loadKnowledgeSources({ silent: true });
      await loadAiDiagnostics({ silent: true });
    } catch (err) {
      console.error('Failed to save knowledge source:', err);
      setBanner({ type: 'error', text: err.message || 'Could not save knowledge source.' });
    } finally {
      setSavingKnowledge(false);
    }
  };

  const handleEditKnowledgeSource = (source) => {
    setActiveKnowledgeTemplate(null);
    setKnowledgeEditorTarget(null);
    setKnowledgeForm({
      id: source.id,
      type: source.type || 'text',
      title: source.title || '',
      raw_text: source.raw_text || '',
      url: source.url || '',
    });
  };

  const handleDeleteKnowledgeSource = async (source) => {
    if (!window.confirm(`Delete knowledge source "${source.title}"?`)) return;
    try {
      setKnowledgeBusyId(source.id);
      await knowledgeApi.deleteSource(source.id);
      if (knowledgeForm.id === source.id) resetKnowledgeForm();
      await loadKnowledgeSources({ silent: true });
      await loadAiDiagnostics({ silent: true });
      setBanner({ type: 'success', text: 'Knowledge source deleted.' });
    } catch (err) {
      console.error('Failed to delete knowledge source:', err);
      setBanner({ type: 'error', text: err.message || 'Could not delete knowledge source.' });
    } finally {
      setKnowledgeBusyId(null);
    }
  };

  const handleReindexKnowledgeSource = async (source) => {
    try {
      setKnowledgeBusyId(source.id);
      await knowledgeApi.reindexSource(source.id);
      await loadKnowledgeSources({ silent: true });
      await loadAiDiagnostics({ silent: true });
      setBanner({ type: 'success', text: `Reindex started for "${source.title}".` });
    } catch (err) {
      console.error('Failed to reindex knowledge source:', err);
      setBanner({ type: 'error', text: err.message || 'Could not reindex knowledge source.' });
    } finally {
      setKnowledgeBusyId(null);
    }
  };

  const handleConnectLinkedIn = async (accountId = null) => {
    try {
      setConnecting(true);
      const { link } = await linkedin.getConnectLink(accountId);
      if (!link) throw new Error('No link returned');
      // Full-page redirect so Unipile's callback comes back to this same tab
      window.location.href = link;
    } catch (err) {
      console.error('Failed to get connect link:', err);
      setBanner({ type: 'error', text: err.message || 'Could not start LinkedIn connection.' });
      setConnecting(false);
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm('Disconnect this account?')) return;
    try {
      await linkedin.disconnectAccount(accountId);
      await loadAccounts();
      await loadAiDiagnostics({ silent: true });
    } catch (err) {
      console.error('Failed to disconnect:', err);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      setBanner({ type: 'error', text: 'Enter your current and new password.' });
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setBanner({ type: 'error', text: 'New password confirmation does not match.' });
      return;
    }
    try {
      setPasswordSaving(true);
      await auth.changePassword(passwordForm.current_password, passwordForm.new_password);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setBanner({ type: 'success', text: 'Password changed successfully.' });
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Password change failed.' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleStart2fa = async () => {
    try {
      setTwoFactorBusy(true);
      setRecoveryCodes([]);
      const data = await userApi.setup2fa();
      setTwoFactorSetup(data);
      setTwoFactorCode('');
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not start 2FA setup.' });
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const handleEnable2fa = async () => {
    try {
      setTwoFactorBusy(true);
      const data = await userApi.enable2fa(twoFactorCode);
      setRecoveryCodes(data.recovery_codes || []);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setBanner({ type: 'success', text: 'Two-factor authentication is enabled.' });
      await loadSecurity();
      await checkAuth();
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not enable 2FA.' });
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const handleDisable2fa = async () => {
    if (!window.confirm('Disable two-factor authentication?')) return;
    try {
      setTwoFactorBusy(true);
      await userApi.disable2fa(twoFactorDisable.current_password, twoFactorDisable.code);
      setTwoFactorDisable({ current_password: '', code: '' });
      setRecoveryCodes([]);
      setBanner({ type: 'success', text: 'Two-factor authentication is disabled.' });
      await loadSecurity();
      await checkAuth();
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not disable 2FA.' });
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      setCalendarBusy(true);
      const { link } = await calendarApi.connectLink('/app/settings');
      if (!link) throw new Error('No Google connect link returned');
      window.location.href = link;
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not start Google Calendar connection.' });
      setCalendarBusy(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try {
      setCalendarBusy(true);
      await calendarApi.disconnectGoogle();
      await loadCalendarStatus();
      setBanner({ type: 'success', text: 'Google Calendar disconnected.' });
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not disconnect Google Calendar.' });
    } finally {
      setCalendarBusy(false);
    }
  };

  const toggleDay = (d) => {
    setAi((prev) => ({
      ...prev,
      working_days: prev.working_days.includes(d)
        ? prev.working_days.filter((x) => x !== d)
        : [...prev.working_days, d].sort(),
    }));
  };

  const effectiveAiSettings = normalizeAiSettings(aiDiagnostics?.settings || ai);
  const latestLinkedAccount = (aiDiagnostics?.linked_accounts || [])
    .slice()
    .sort((a, b) => new Date(b.last_sync_at || 0).getTime() - new Date(a.last_sync_at || 0).getTime())[0];
  const nextPendingAiJob = (aiDiagnostics?.pending_jobs || [])[0];
  const summary = aiDiagnostics?.derived_status_summary || {};
  const latestIssue = summary.latest_skip_or_failure;
  const syncDebug = latestLinkedAccount?.last_sync_debug || null;
  const knowledgeSummary = aiDiagnostics?.knowledge_base || {};
  const knowledgeLatestError = knowledgeSummary.latest_error || null;
  const matchedKnowledgeTemplate = findKnowledgeTemplateByTitle(knowledgeForm.title);
  const knowledgeSimilarRoleExists = !!matchedKnowledgeTemplate && knowledgeSources.some(
    (source) => source.id !== knowledgeForm.id
      && normalizeKnowledgeTitle(source.title) === normalizeKnowledgeTitle(matchedKnowledgeTemplate.title),
  );
  const knowledgeReadinessLabel = knowledgeLatestError?.last_error
    ? 'Indexing error'
    : (knowledgeSummary.ready_sources ?? 0) > 0
      ? 'Ready sources found'
      : 'No ready sources';
  const knowledgeReadinessHint = knowledgeLatestError?.last_error
    ? knowledgeLatestError.last_error
    : (knowledgeSummary.ready_sources ?? 0) > 0
      ? `${knowledgeSummary.indexed_chunks ?? 0} indexed chunks are available for grounded replies.`
      : 'Add and index grounded sources before relying on auto-replies.';
  const statusToneClass = summary.status?.startsWith('blocked')
    ? 'text-[#B42318]'
    : summary.status === 'waiting_for_quiet_window'
      ? 'text-[#B35900]'
      : 'text-[#1E7F4E]';
  const quietWindowModeLabel = effectiveAiSettings.variable_delay
    ? `Variable ${effectiveAiSettings.variable_delay_min}-${effectiveAiSettings.variable_delay_max} min`
    : `Fixed ${effectiveAiSettings.response_delay_minutes} min`;

  return (
    <div className="app-page-shell bg-white" data-testid="settings-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center px-6 sticky top-0 bg-white z-10">
        <h1 className="text-lg font-semibold text-[#191919]">Settings</h1>
      </header>

      <div className="app-page-scroll">
        <div className="mx-auto w-full max-w-2xl space-y-6 px-4 py-5 pb-28 sm:p-6">
        {banner && (
          <div
            className={`p-3 rounded-lg text-sm border ${
              banner.type === 'success'
                ? 'bg-[#E6F4EA] border-[#C5E7D2] text-[#1E7F4E]'
                : 'bg-[#FFEBE9] border-[#FF8080] text-[#CE3636]'
            }`}
            data-testid="settings-banner"
          >
            {banner.text}
          </div>
        )}

        {/* Profile */}
        <section className="bg-white border border-[#E3E2E0] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#F0F0EF] rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-[#6B6B6B]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#191919]">Profile</h2>
              <p className="text-xs text-[#6B6B6B]">Your account information</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-[#191919] mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                data-testid="settings-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#191919] mb-1">Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm text-[#6B6B6B]"
              />
            </div>
            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saved ? 'Saved' : 'Save'}
            </button>
          </div>
        </section>

        {/* Security */}
        <section id="security" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5" data-testid="security-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#FFF4E5] rounded-lg flex items-center justify-center">
              <Lock className="w-4 h-4 text-[#B35900]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#191919]">Security</h2>
              <p className="text-xs text-[#6B6B6B]">Password and authenticator app protection</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase text-[#6B6B6B]">Change password</h3>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))}
                placeholder="Current password"
                className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                data-testid="current-password"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))}
                  placeholder="New password"
                  className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                  data-testid="new-password"
                />
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                  data-testid="confirm-password"
                />
              </div>
              <button
                onClick={handleChangePassword}
                disabled={passwordSaving}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="change-password-btn"
              >
                {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Update password
              </button>
            </div>

            <div className="border-t border-[#F0F0EF] pt-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-xs font-semibold uppercase text-[#6B6B6B]">Two-factor authentication</h3>
                  <p className="mt-1 text-sm text-[#191919]">
                    {security.two_factor_enabled ? 'Enabled' : 'Not enabled'}
                    {security.two_factor_enabled ? ` - ${security.recovery_codes_remaining || 0} recovery codes left` : ''}
                  </p>
                </div>
                {!security.two_factor_enabled && (
                  <button
                    onClick={handleStart2fa}
                    disabled={twoFactorBusy}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                    data-testid="start-2fa-btn"
                  >
                    {twoFactorBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    Enable 2FA
                  </button>
                )}
              </div>

              {twoFactorSetup && (
                <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-3 space-y-3" data-testid="2fa-setup">
                  <p className="text-sm text-[#191919]">Add this account in your authenticator app, then enter the 6-digit code.</p>
                  <div className="flex justify-center rounded-lg border border-[#E3E2E0] bg-white p-4">
                    <QRCodeSVG
                      value={twoFactorSetup.otpauth_url}
                      size={176}
                      level="M"
                      includeMargin
                      aria-label="Scan this QR code with your authenticator app"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] text-[#6B6B6B] mb-1">Manual secret</p>
                    <code className="block break-all rounded-md bg-white border border-[#E3E2E0] px-2 py-1.5 text-xs text-[#191919]">
                      {twoFactorSetup.secret}
                    </code>
                  </div>
                  <details className="text-xs text-[#6B6B6B]">
                    <summary className="cursor-pointer">Authenticator URI</summary>
                    <code className="mt-2 block break-all rounded-md bg-white border border-[#E3E2E0] px-2 py-1.5 text-[11px]">
                      {twoFactorSetup.otpauth_url}
                    </code>
                  </details>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      placeholder="123456"
                      className="flex-1 px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
                      data-testid="2fa-enable-code"
                    />
                    <button
                      onClick={handleEnable2fa}
                      disabled={twoFactorBusy || !twoFactorCode}
                      className="inline-flex items-center justify-center gap-2 px-3.5 py-2 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                      data-testid="enable-2fa-btn"
                    >
                      {twoFactorBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Verify
                    </button>
                  </div>
                </div>
              )}

              {recoveryCodes.length > 0 && (
                <div className="rounded-lg border border-[#C5E7D2] bg-[#E6F4EA] p-3" data-testid="recovery-codes">
                  <p className="text-sm font-medium text-[#1E7F4E]">Save these recovery codes now. They are shown once.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {recoveryCodes.map((code) => (
                      <code key={code} className="rounded bg-white/80 px-2 py-1 text-xs text-[#191919]">{code}</code>
                    ))}
                  </div>
                </div>
              )}

              {security.two_factor_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                  <input
                    type="password"
                    value={twoFactorDisable.current_password}
                    onChange={(e) => setTwoFactorDisable((prev) => ({ ...prev, current_password: e.target.value }))}
                    placeholder="Current password"
                    className="px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="2fa-disable-password"
                  />
                  <input
                    type="text"
                    value={twoFactorDisable.code}
                    onChange={(e) => setTwoFactorDisable((prev) => ({ ...prev, code: e.target.value }))}
                    placeholder="2FA or recovery code"
                    className="px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="2fa-disable-code"
                  />
                  <button
                    onClick={handleDisable2fa}
                    disabled={twoFactorBusy}
                    className="inline-flex items-center justify-center gap-2 px-3.5 py-2 border border-[#FF8080] text-[#CE3636] text-sm font-medium rounded-lg hover:bg-[#FFEBE9] disabled:opacity-50"
                    data-testid="disable-2fa-btn"
                  >
                    Disable
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* OpenAI - admin self only */}
        {user?.role === 'admin' && (
        <section className="bg-white border border-[#E3E2E0] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1E7F4E]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#191919]">OpenAI Integration</h2>
              <p className="text-xs text-[#6B6B6B]">Used for AI-powered drafts and replies</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#191919] mb-1">OpenAI API Key</label>
            <input
              type="password"
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder="sk-..."
              className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
              data-testid="openai-key"
            />
            <p className="text-xs text-[#6B6B6B] mt-2">
              Get your key from{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#0052CC] hover:underline">
                OpenAI Dashboard
              </a>
            </p>
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleSaveOpenAI}
                disabled={savingOpenAI || !openaiKey || isMaskedKey(openaiKey)}
                className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="save-openai-btn"
              >
                {savingOpenAI ? <Loader2 className="w-4 h-4 animate-spin" /> : openaiSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {openaiSaved ? 'Saved' : 'Save key'}
              </button>
              {user?.settings?.openai_api_key_configured && (
                <button
                  onClick={handleClearOpenAI}
                  className="text-xs text-[#CE3636] hover:underline"
                  data-testid="clear-openai-btn"
                >
                  Remove key
                </button>
              )}
            </div>
          </div>
        </section>
        )}

        {/* Notice for clients */}
        {user?.role !== 'admin' && (
          <section className="bg-[#FBFBFA] border border-dashed border-[#E3E2E0] rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-[#E6F4EA] rounded-lg flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-[#1E7F4E]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#191919]">AI is managed by your admin</h2>
                <p className="text-xs text-[#6B6B6B] mt-1">
                  Your OpenAI access is configured by your administrator. AI features (Rewrite, Auto-Reply) are ready to use as long as the admin has assigned you a key.
                </p>
              </div>
            </div>
          </section>
        )}

        <section id="knowledge-base" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5" data-testid="knowledge-base-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#EEF2FF] rounded-lg flex items-center justify-center">
              <Database className="w-4 h-4 text-[#4338CA]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#191919]">Knowledge Base</h2>
              <p className="text-xs text-[#6B6B6B]">
                Add grounded client facts for AI replies. Text and explicit URLs only in this POC.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-4 mb-4">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-[#191919]">Starter templates</h3>
              <p className="text-xs text-[#6B6B6B] mt-0.5">
                One-click starting points for hard AI rules, business scope, and approved reply patterns.
              </p>
            </div>
            <div className="space-y-3">
              {KB_STARTER_TEMPLATES.map((template) => {
                const hasSimilarSource = knowledgeSources.some(
                  (source) => normalizeKnowledgeTitle(source.title) === normalizeKnowledgeTitle(template.title),
                );
                return (
                  <div key={template.key} data-knowledge-template-card className="rounded-lg border border-[#E3E2E0] bg-white p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold text-[#191919]">{template.title}</h4>
                      <span className="inline-flex items-center rounded-full bg-[#F4F4F3] px-2 py-0.5 text-[11px] font-medium text-[#4B4B4B]">
                        {template.language}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-[#6B6B6B]">{template.description}</p>
                    {hasSimilarSource ? (
                      <p className="mt-2 text-[11px] leading-5 text-[#B35900]">
                        A note with a similar role already exists. Inserting this template will not overwrite it.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={(event) => handleInsertKnowledgeTemplate(
                        template,
                        event.currentTarget.closest('[data-knowledge-template-card]')
                          ?.querySelector('[data-knowledge-template-editor]'),
                      )}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#E3E2E0] px-3 py-2 text-xs font-medium text-[#191919] hover:bg-[#FBFBFA]"
                      data-testid={`insert-template-${template.key}`}
                    >
                      <Save className="w-3.5 h-3.5" />
                      Insert as text note
                    </button>
                    <div data-knowledge-template-editor />
                  </div>
                );
              })}
            </div>
          </div>

          <div ref={defaultKnowledgeEditorRef} />
          {knowledgeEditorReady && (activeKnowledgeTemplate || knowledgeForm.id) && createPortal(
          <div className={`rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-4 mb-4 ${activeKnowledgeTemplate ? 'mt-3' : ''}`}>
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setKnowledgeForm((prev) => ({ ...prev, type: 'text', url: '' }))}
                className={`px-3 py-1.5 text-xs rounded-lg border ${knowledgeForm.type === 'text' ? 'bg-[#0A0A0A] text-white border-transparent' : 'bg-white text-[#6B6B6B] border-[#E3E2E0]'}`}
                data-testid="knowledge-type-text"
              >
                Text note
              </button>
              <button
                onClick={() => setKnowledgeForm((prev) => ({ ...prev, type: 'url', raw_text: '' }))}
                className={`px-3 py-1.5 text-xs rounded-lg border ${knowledgeForm.type === 'url' ? 'bg-[#0A0A0A] text-white border-transparent' : 'bg-white text-[#6B6B6B] border-[#E3E2E0]'}`}
                data-testid="knowledge-type-url"
              >
                URL page
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1">Title</label>
                <input
                  type="text"
                  value={knowledgeForm.title}
                  onChange={(e) => setKnowledgeForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Services and pricing guardrails"
                  className="w-full px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
                  data-testid="knowledge-title"
                />
                {matchedKnowledgeTemplate ? (
                  <p className="text-[11px] text-[#6B6B6B] mt-1">
                    Recommended language: {matchedKnowledgeTemplate.language}. This starter note is meant to be reviewed and edited before saving.
                  </p>
                ) : null}
              </div>

              {knowledgeForm.type === 'text' ? (
                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1">Knowledge text</label>
                  <textarea
                    value={knowledgeForm.raw_text}
                    onChange={(e) => setKnowledgeForm((prev) => ({ ...prev, raw_text: e.target.value }))}
                    placeholder="Add services, positioning, FAQs, pricing guardrails, and what AI is allowed to say."
                    rows={6}
                    className="w-full px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm resize-y"
                    data-testid="knowledge-raw-text"
                  />
                  {matchedKnowledgeTemplate ? (
                    <p className="text-[11px] text-[#6B6B6B] mt-1">
                      {matchedKnowledgeTemplate.title === 'AI Reply Rules'
                        ? 'Keep this note in English and use it for hard behavior rules, not business facts.'
                        : 'Keep this note in English and fill in concrete services, scope limits, and approved wording.'}
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#6B6B6B] mt-1">
                      Text notes work best for strict rules, supported services, FAQs, and approved examples.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1">URL</label>
                  <input
                    type="url"
                    value={knowledgeForm.url}
                    onChange={(e) => setKnowledgeForm((prev) => ({ ...prev, url: e.target.value }))}
                    placeholder="https://example.com/services"
                    className="w-full px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="knowledge-url"
                  />
                  <p className="text-[11px] text-[#6B6B6B] mt-1">
                    We index only this exact page. No site crawling in v1.
                  </p>
                </div>
              )}

              {knowledgeSimilarRoleExists ? (
                <div className="rounded-lg border border-[#FFD9A8] bg-[#FFF4E5] px-3 py-2 text-xs text-[#7A3E00]">
                  A note with a similar role already exists. Saving this will create a separate source, not replace the old one.
                </div>
              ) : null}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveKnowledgeSource}
                  disabled={savingKnowledge || !knowledgeForm.title.trim() || (knowledgeForm.type === 'text' ? !knowledgeForm.raw_text.trim() : !knowledgeForm.url.trim())}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid="save-knowledge-source-btn"
                >
                  {savingKnowledge ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {knowledgeForm.id ? 'Update source' : 'Add source'}
                </button>
                {(knowledgeForm.id || activeKnowledgeTemplate) ? (
                  <button
                    onClick={resetKnowledgeForm}
                    className="text-xs text-[#6B6B6B] hover:underline"
                    data-testid="cancel-knowledge-edit-btn"
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          knowledgeEditorTarget || defaultKnowledgeEditorRef.current,
          )}

          {loadingKnowledge ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#6B6B6B]" />
            </div>
          ) : knowledgeSources.length === 0 ? (
            <div className="text-center py-6 border-2 border-dashed border-[#E3E2E0] rounded-lg">
              <p className="text-sm text-[#6B6B6B]">No knowledge sources yet.</p>
              <p className="text-xs text-[#9B9B9B] mt-1">AI will stay strict and conservative until you add grounded sources.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {knowledgeSources.map((source) => {
                const busy = knowledgeBusyId === source.id;
                const statusColor = source.status === 'ready'
                  ? 'text-[#1E7F4E] bg-[#E6F4EA]'
                  : source.status === 'error'
                    ? 'text-[#B42318] bg-[#FEF3F2]'
                    : 'text-[#7A3E00] bg-[#FFF4E5]';
                const expanded = expandedKnowledgeSource === source.id;
                return (
                  <div key={source.id} className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-4">
                    <button
                      type="button"
                      onClick={() => setExpandedKnowledgeSource(expanded ? null : source.id)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                      aria-expanded={expanded}
                      data-testid={`toggle-knowledge-${source.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-sm font-semibold text-[#191919]">{source.title}</h3>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor}`}>
                            {KNOWLEDGE_STATUS_LABELS[source.status] || source.status}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-[#6B6B6B]">
                          {source.type === 'url' ? 'URL source' : 'Text note'} · {source.chunk_count || 0} chunks · {formatKnowledgeMoment(source.last_indexed_at)}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-medium text-[#4B4B4B]">
                        {expanded ? 'Hide' : 'View'}
                      </span>
                    </button>

                    {expanded ? (
                    <div className="mt-4 border-t border-[#E3E2E0] pt-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditKnowledgeSource(source);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#E3E2E0] rounded-lg text-xs text-[#191919] hover:bg-white"
                          data-testid={`edit-knowledge-${source.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleReindexKnowledgeSource(source);
                          }}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#E3E2E0] rounded-lg text-xs text-[#191919] hover:bg-white disabled:opacity-50"
                          data-testid={`reindex-knowledge-${source.id}`}
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                          Reindex
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteKnowledgeSource(source);
                          }}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#FFD0D5] rounded-lg text-xs text-[#B42318] hover:bg-[#FEF3F2] disabled:opacity-50"
                          data-testid={`delete-knowledge-${source.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>

                    <div className="mt-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B6B6B]">
                        {source.type === 'url' ? 'URL' : 'Text'}
                      </p>
                      {source.type === 'url' ? (
                        <p className="mt-2 break-all text-sm leading-6 text-[#343434]">{source.url}</p>
                      ) : (
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-[#343434]">
                          {source.raw_text || 'No text saved.'}
                        </p>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 border-t border-[#E3E2E0] pt-4 text-xs">
                      <div>
                        <p className="text-[#8A8A8A]">Last indexed</p>
                        <p className="mt-1 font-medium text-[#4B4B4B]">{formatKnowledgeMoment(source.last_indexed_at)}</p>
                      </div>
                      <div>
                        <p className="text-[#8A8A8A]">Chunks</p>
                        <p className="mt-1 font-medium text-[#4B4B4B]">{source.chunk_count || 0}</p>
                      </div>
                    </div>

                    {source.last_error ? (
                      <div className="mt-3 rounded-lg border border-[#F4C7C3] bg-[#FEF3F2] px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#B42318]">Indexing error</p>
                        <p className="mt-1 break-words text-xs leading-5 text-[#B42318]">{source.last_error}</p>
                      </div>
                    ) : null}
                    </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <EngagementVoiceSettings />

        {/* LinkedIn accounts */}
        <section id="linkedin-accounts" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#E6EFFF] rounded-lg flex items-center justify-center">
                <Link2 className="w-4 h-4 text-[#0052CC]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#191919]">LinkedIn Accounts</h2>
                <p className="text-xs text-[#6B6B6B]">Connect via Unipile hosted auth</p>
              </div>
            </div>
            <button
              onClick={() => handleConnectLinkedIn()}
              disabled={connecting}
              aria-label={connecting ? 'Starting LinkedIn connection' : 'Connect'}
              className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0052CC] hover:bg-[#0747A6] text-white text-[0] after:content-['Connect'] after:text-sm font-medium rounded-lg disabled:opacity-50"
              data-testid="connect-linkedin-btn"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              {connecting ? 'Starting…' : 'Connect LinkedIn'}
            </button>
          </div>

          {loadingAccounts ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#6B6B6B]" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-6 border-2 border-dashed border-[#E3E2E0] rounded-lg">
              <p className="text-sm text-[#6B6B6B]">No accounts connected yet.</p>
              <p className="text-xs text-[#9B9B9B] mt-1">Connect an account to sync your inbox.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between p-3 bg-[#FBFBFA] rounded-lg">
                  <div className="flex items-center gap-3">
                    {account.profile_picture ? (
                      <img src={account.profile_picture} alt={account.name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 bg-[#E3E2E0] rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-[#6B6B6B]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[#191919]">{account.name || 'LinkedIn Account'}</p>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${!account.needs_reconnect && (account.is_valid || account.status === 'connected') ? 'bg-[#1E7F4E]' : 'bg-[#CE3636]'}`} />
                        <span className="text-xs text-[#6B6B6B]">{account.needs_reconnect ? 'Reconnect required' : account.is_valid || account.status === 'connected' ? 'Connected' : 'Disconnected'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {account.needs_reconnect && (
                      <button
                        onClick={() => handleConnectLinkedIn(account.unipile_account_id)}
                        disabled={connecting}
                        className="rounded-lg bg-[#0B1D3A] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        {connecting ? 'Starting...' : 'Reconnect'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDisconnect(account.id)}
                      className="p-1.5 hover:bg-[#E3E2E0] rounded-lg"
                      data-testid={`disconnect-${account.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-[#CE3636]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Google Calendar */}
        <section id="google-calendar" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5" data-testid="google-calendar-section">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-[#E6F4EA] rounded-lg flex items-center justify-center shrink-0">
                <CalendarDays className="w-4 h-4 text-[#1E7F4E]" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-[#191919]">Google Calendar</h2>
                <p className="text-xs text-[#6B6B6B] truncate">
                  {calendarStatus?.connected
                    ? `Connected as ${calendarStatus.email || 'Google account'}`
                    : calendarStatus?.needs_reconnect
                      ? 'Authorization expired. Reconnect Google Calendar.'
                    : calendarStatus?.configured === false
                      ? 'OAuth credentials are missing on the backend.'
                      : 'Connect to create bookings and Meet links.'}
                </p>
              </div>
            </div>
            {calendarStatus?.connected ? (
              <button
                onClick={handleDisconnectCalendar}
                disabled={calendarBusy}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#E3E2E0] hover:bg-[#F7F7F5] text-[#191919] text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="disconnect-google-calendar-btn"
              >
                {calendarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnectCalendar}
                disabled={calendarBusy || calendarStatus?.configured === false}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="connect-google-calendar-btn"
              >
                {calendarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {calendarStatus?.needs_reconnect ? 'Reconnect' : 'Connect'}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg bg-[#FBFBFA] border border-[#E3E2E0] px-3 py-2">
              <span className="block text-[#6B6B6B]">Backend config</span>
              <span className="font-medium text-[#191919]">{calendarStatus?.configured ? 'Configured' : 'Missing'}</span>
            </div>
            <div className="rounded-lg bg-[#FBFBFA] border border-[#E3E2E0] px-3 py-2">
              <span className="block text-[#6B6B6B]">Calendar</span>
              <span className="font-medium text-[#191919]">{calendarStatus?.connected ? (calendarStatus.calendar_summary || calendarStatus.calendar_id || 'primary') : calendarStatus?.needs_reconnect ? 'Reconnect required' : 'Not connected'}</span>
            </div>
          </div>
        </section>

        {/* AI Autoresponder */}
        <section id="ai-auto-reply" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5" data-testid="ai-autoresponder-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#F0E6FF] rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#6B00B3]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#191919]">AI Auto-Reply</h2>
                <p className="text-xs text-[#6B6B6B]">Configure when and how AI responds</p>
              </div>
            </div>
            <Toggle
              checked={ai.enabled}
              onChange={handleToggleAiEnabled}
              disabled={savingAi}
              testid="ai-enabled-toggle"
            />
          </div>

          <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-4 mb-5" data-testid="ai-diagnostics-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-[#191919]">Runtime diagnostics</h3>
                <p className="text-xs text-[#6B6B6B] mt-0.5">
                  Local AI replies should run without manual Sync. This shows what the scheduler is currently waiting on.
                </p>
              </div>
              {loadingAiDiagnostics ? <Loader2 className="w-4 h-4 animate-spin text-[#6B6B6B]" /> : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-xs">
              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Status</span>
                <span className={`block font-medium mt-0.5 ${statusToneClass}`}>
                  {AI_DIAGNOSTIC_STATUS_LABELS[summary.status] || 'Loading diagnostics'}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {humanizeAiReason(summary.reason)}
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Effective mode</span>
                <span className="block font-medium text-[#191919] mt-0.5">{quietWindowModeLabel}</span>
                <span className="block text-[#6B6B6B] mt-1">
                  Poll {aiDiagnostics?.runtime?.message_sync_interval_seconds ?? 'â€”'}s / dispatcher {aiDiagnostics?.runtime?.ai_reply_job_dispatch_interval_seconds ?? 'â€”'}s
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Last auto-sync</span>
                <span className="block font-medium text-[#191919] mt-0.5">
                  {formatDiagnosticMoment(latestLinkedAccount?.last_sync_at)}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {latestLinkedAccount?.name || 'No connected LinkedIn account'}
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Next pending reply</span>
                <span className="block font-medium text-[#191919] mt-0.5">
                  {nextPendingAiJob ? formatDiagnosticMoment(nextPendingAiJob.due_at) : 'No pending AI reply job'}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {nextPendingAiJob
                    ? `${nextPendingAiJob.quiet_window_minutes || effectiveAiSettings.response_delay_minutes} min quiet window`
                    : humanizeAiReason(summary.reason)}
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2 sm:col-span-2">
                <span className="block text-[#6B6B6B]">Latest skip or failure</span>
                <span className="block font-medium text-[#191919] mt-0.5">
                  {latestIssue ? humanizeAiReason(latestIssue.reason) : 'No recent AI skip or failure'}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {latestIssue?.updated_at
                    ? `Updated ${formatDiagnosticMoment(latestIssue.updated_at)}`
                    : latestLinkedAccount?.last_sync_error || aiDiagnostics?.load_error || 'Diagnostics are healthy.'}
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Knowledge readiness</span>
                <span className="block font-medium text-[#191919] mt-0.5">
                  {knowledgeReadinessLabel}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {knowledgeSummary.ready_sources ?? 0} ready / {knowledgeSummary.total_sources ?? 0} total
                </span>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-white px-3 py-2">
                <span className="block text-[#6B6B6B]">Knowledge issue</span>
                <span className="block font-medium text-[#191919] mt-0.5">
                  {knowledgeLatestError?.title || 'No recent indexing error'}
                </span>
                <span className="block text-[#6B6B6B] mt-1">
                  {knowledgeReadinessHint}
                </span>
              </div>
            </div>

            {syncDebug ? (
              <div className="mt-3 text-[11px] text-[#6B6B6B]">
                Latest poll checked {syncDebug.messages_seen || 0} messages, inserted {syncDebug.messages_inserted || 0}, scheduled {syncDebug.ai_scheduled || 0}, postponed {syncDebug.ai_postponed || 0}, approvals {syncDebug.ai_approval_created || 0}, sent {syncDebug.ai_sent || 0}, failed {syncDebug.ai_failed || 0}.
              </div>
            ) : null}
            {latestLinkedAccount?.last_sync_error ? (
              <div className="mt-3 rounded-lg border border-[#FFD9A8] bg-[#FFF4E5] px-3 py-2 text-xs text-[#7A3E00]">
                Last auto-sync error: {latestLinkedAccount.last_sync_error}
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-4 mb-5" data-testid="kb-recommended-defaults">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-[#191919]">Recommended for KB mode</h3>
                <p className="text-xs text-[#6B6B6B] mt-0.5">
                  Pre-fills a stricter setup for grounded replies: threshold 0.80, low-confidence to Approvals, and concise factual instructions.
                </p>
              </div>
              <button
                type="button"
                onClick={applyRecommendedKbDefaults}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E3E2E0] rounded-lg text-xs font-medium text-[#191919] hover:bg-white"
                data-testid="apply-kb-defaults-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Apply recommended defaults
              </button>
            </div>
          </div>

          {ai.enabled && (
            <div className="space-y-5">
              {/* Tone */}
              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1.5">Tone</label>
                <div className="flex gap-2 flex-wrap">
                  {TONES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setAi({ ...ai, tone: t.value })}
                      className={`px-3 py-1.5 text-xs rounded-lg border ${
                        ai.tone === t.value
                          ? 'bg-[#0A0A0A] text-white border-transparent'
                          : 'bg-white text-[#6B6B6B] border-[#E3E2E0] hover:bg-[#F0F0EF]'
                      }`}
                      data-testid={`ai-tone-${t.value}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Triggers */}
              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1.5">Trigger on</label>
                <div className="space-y-2">
                  <CheckRow label="First message from a new contact" checked={ai.trigger_first_message} onChange={(v) => setAi({ ...ai, trigger_first_message: v })} testid="trigger-first" />
                  <CheckRow label="Replies within an existing conversation" checked={ai.trigger_reply} onChange={(v) => setAi({ ...ai, trigger_reply: v })} testid="trigger-reply" />
                </div>
                <p className="text-xs text-[#6B6B6B] mt-2">Connection-invite auto-replies are deferred for now and intentionally hidden until the backend wiring exists.</p>
              </div>

              <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#191919]">Existing unresolved chats</p>
                    {backfillResult ? (
                      <p className="text-xs text-[#6B6B6B] mt-0.5">
                        Checked {backfillResult.checked || 0} / queued {backfillResult.queued || 0} / sent {backfillResult.sent || 0}
                      </p>
                    ) : (
                      <p className="text-xs text-[#6B6B6B] mt-0.5">Generate pending drafts for chats marked Needs Reply.</p>
                    )}
                  </div>
                  <button
                    onClick={handleBackfillAi}
                    disabled={backfillingAi || !ai.enabled}
                    className="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 border border-[#E3E2E0] bg-white hover:bg-[#F0F0EF] text-[#191919] text-sm font-medium rounded-lg disabled:opacity-50"
                    data-testid="ai-backfill-btn"
                  >
                    {backfillingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Generate drafts
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1.5">Replies are sent as</label>
                <div className="flex gap-2 flex-wrap">
                  {REPLY_PERSONAS.map((persona) => (
                    <button
                      key={persona.value}
                      onClick={() => setAi({ ...ai, reply_persona: persona.value })}
                      className={`px-3 py-1.5 text-xs rounded-lg border ${
                        ai.reply_persona === persona.value
                          ? 'bg-[#0A0A0A] text-white border-transparent'
                          : 'bg-white text-[#6B6B6B] border-[#E3E2E0] hover:bg-[#F0F0EF]'
                      }`}
                      data-testid={`ai-reply-persona-${persona.value}`}
                    >
                      {persona.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#6B6B6B] mt-2">
                  Used for Slovak and Czech grammar, self-reference, and greeting style in AI replies.
                </p>
              </div>

              {/* Quiet window */}
              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1.5">Quiet window</label>
                <div className="flex items-center gap-3 mb-2">
                  <label className="flex items-center gap-2 text-xs text-[#191919] cursor-pointer">
                    <input
                      type="radio"
                      checked={!ai.variable_delay}
                      onChange={() => setAi({ ...ai, variable_delay: false })}
                      className="accent-[#0A0A0A]"
                      data-testid="delay-fixed"
                    />
                    Fixed
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[#191919] cursor-pointer">
                    <input
                      type="radio"
                      checked={!!ai.variable_delay}
                      onChange={() => setAi({
                        ...ai,
                        variable_delay: true,
                        variable_delay_min: clampQuietWindow(ai.variable_delay_min || ai.response_delay_minutes || 5),
                        variable_delay_max: clampRangeMax(ai.variable_delay_max || 30, clampQuietWindow(ai.variable_delay_min || ai.response_delay_minutes || 5)),
                      })}
                      className="accent-[#0A0A0A]"
                      data-testid="delay-variable"
                    />
                    Variable
                  </label>
                </div>
                {!ai.variable_delay ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={5}
                      max={30}
                      value={ai.response_delay_minutes}
                      onChange={(e) => setAi({ ...ai, response_delay_minutes: clampQuietWindow(e.target.value) })}
                      className="w-24 px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                      data-testid="ai-delay-input"
                    />
                    <span className="text-xs text-[#6B6B6B]">minutes</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#6B6B6B]">Random between</span>
                    <input
                      type="number"
                      min={5}
                      max={30}
                      value={ai.variable_delay_min}
                      onChange={(e) => {
                        const nextMin = clampQuietWindow(e.target.value);
                        setAi({
                          ...ai,
                          variable_delay_min: nextMin,
                          variable_delay_max: clampRangeMax(ai.variable_delay_max, nextMin),
                        });
                      }}
                      className="w-20 px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                      data-testid="ai-delay-min"
                    />
                    <span className="text-xs text-[#6B6B6B]">and</span>
                    <input
                      type="number"
                      min={Math.max(5, ai.variable_delay_min || 5)}
                      max={30}
                      value={ai.variable_delay_max}
                      onChange={(e) => setAi({
                        ...ai,
                        variable_delay_max: clampRangeMax(e.target.value, Math.max(5, ai.variable_delay_min || 5)),
                      })}
                      className="w-20 px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                      data-testid="ai-delay-max"
                    />
                    <span className="text-xs text-[#6B6B6B]">minutes</span>
                  </div>
                )}
                <p className="text-xs text-[#6B6B6B] mt-2">
                  AI always waits for at least 5 minutes of silence in the chat. In variable mode, each new burst picks a random delay in your range and resets if another inbound message arrives.
                </p>
              </div>

              {/* Working hours */}
              <div className="border-t border-[#F0F0EF] pt-5">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 text-xs font-medium text-[#191919]">
                    <Clock className="w-4 h-4" /> Working hours
                  </label>
                  <Toggle checked={ai.working_hours_enabled} onChange={(v) => setAi({ ...ai, working_hours_enabled: v })} testid="working-hours-toggle" />
                </div>
                {ai.working_hours_enabled && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-[#6B6B6B] mb-1">From</label>
                        <input
                          type="time"
                          value={ai.working_hours_start}
                          onChange={(e) => setAi({ ...ai, working_hours_start: e.target.value })}
                          className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                          data-testid="wh-start"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-[#6B6B6B] mb-1">To</label>
                        <input
                          type="time"
                          value={ai.working_hours_end}
                          onChange={(e) => setAi({ ...ai, working_hours_end: e.target.value })}
                          className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                          data-testid="wh-end"
                        />
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {DAY_LABELS.map((d, i) => (
                        <button
                          key={d}
                          onClick={() => toggleDay(i)}
                          className={`px-2.5 py-1 text-[11px] rounded-md border ${
                            ai.working_days.includes(i)
                              ? 'bg-[#0A0A0A] text-white border-transparent'
                              : 'bg-white text-[#6B6B6B] border-[#E3E2E0]'
                          }`}
                          data-testid={`day-${d}`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Approval + safety */}
              <div className="border-t border-[#F0F0EF] pt-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#1E7F4E] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#191919]">Require human approval</p>
                      <p className="text-xs text-[#6B6B6B]">
                        {ai.require_approval
                          ? 'Drafts go to Approvals instead of auto-sending.'
                          : 'AI replies will send automatically after generation.'}
                      </p>
                    </div>
                  </div>
                  <Toggle checked={ai.require_approval} onChange={(v) => setAi({ ...ai, require_approval: v })} testid="require-approval-toggle" />
                </div>

                {!ai.require_approval && (
                  <div className="ml-6 rounded-lg border border-[#FFD9A8] bg-[#FFF4E5] px-3 py-2 text-xs text-[#7A3E00]" data-testid="autosend-warning">
                    Auto-send is active. After the quiet window passes, new inbound LinkedIn message bursts can be answered without manual approval unless AI reply is disabled on that chat.
                  </div>
                )}

                {ai.require_approval && (
                  <div className="ml-6 pl-2 border-l-2 border-[#E3E2E0]">
                    <p className="text-xs font-medium text-[#191919] mb-2">How would you like to approve drafts?</p>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!ai.approval_via_email}
                          onChange={() => setAi({ ...ai, approval_via_email: false })}
                          className="accent-[#0A0A0A] mt-0.5"
                          data-testid="approval-in-app"
                        />
                        <div>
                          <p className="text-sm text-[#191919]">In-app approval queue</p>
                          <p className="text-[11px] text-[#6B6B6B]">Drafts wait in Linirio until you approve them.</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!!ai.approval_via_email}
                          onChange={() => setAi({ ...ai, approval_via_email: true })}
                          className="accent-[#0A0A0A] mt-0.5"
                          data-testid="approval-via-email"
                        />
                        <div>
                          <p className="text-sm text-[#191919]">Email me with Yes / No buttons</p>
                          <p className="text-[11px] text-[#6B6B6B]">Approve from your inbox without opening Linirio.</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1.5">
                    Confidence threshold: {ai.confidence_threshold.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={ai.confidence_threshold}
                    onChange={(e) => setAi({ ...ai, confidence_threshold: parseFloat(e.target.value) })}
                    className="w-full"
                    data-testid="confidence-threshold"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    Lower values send more often. Higher values are stricter and more likely to use your fallback, queue for approval, or skip.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-2">When confidence is below threshold</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={ai.low_confidence_action === 'fallback'}
                        onChange={() => setAi({ ...ai, low_confidence_action: 'fallback' })}
                        className="accent-[#0A0A0A] mt-0.5"
                        data-testid="low-confidence-fallback"
                      />
                      <div>
                        <p className="text-sm text-[#191919]">Use fallback message</p>
                        <p className="text-[11px] text-[#6B6B6B]">Safest for auto-send if you want a controlled default reply.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={ai.low_confidence_action === 'approval'}
                        onChange={() => setAi({ ...ai, low_confidence_action: 'approval' })}
                        className="accent-[#0A0A0A] mt-0.5"
                        data-testid="low-confidence-approval"
                      />
                      <div>
                        <p className="text-sm text-[#191919]">Send draft to Approvals</p>
                        <p className="text-[11px] text-[#6B6B6B]">Good when AI produced something usable, but you want a human to review weaker cases.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={ai.low_confidence_action === 'skip'}
                        onChange={() => setAi({ ...ai, low_confidence_action: 'skip' })}
                        className="accent-[#0A0A0A] mt-0.5"
                        data-testid="low-confidence-skip"
                      />
                      <div>
                        <p className="text-sm text-[#191919]">Skip reply</p>
                        <p className="text-[11px] text-[#6B6B6B]">Most conservative option. Nothing is sent if confidence is too low.</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1.5">Fallback message (optional)</label>
                  <input
                    type="text"
                    value={ai.fallback_message}
                    onChange={(e) => setAi({ ...ai, fallback_message: e.target.value })}
                    placeholder="Thanks for reaching out — I'll get back to you shortly."
                    className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="ai-fallback"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1.5">Custom instructions (optional)</label>
                  <textarea
                    value={ai.custom_instructions || ''}
                    onChange={(e) => setAi({ ...ai, custom_instructions: e.target.value })}
                    placeholder="e.g. Always mention we offer a 14-day free trial. Never give discounts."
                    rows={3}
                    className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm resize-none"
                    data-testid="ai-instructions"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveAi}
                disabled={savingAi}
                className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="save-ai-btn"
              >
                {savingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : aiSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {aiSaved ? 'Saved' : 'Save AI settings'}
              </button>
            </div>
          )}
        </section>

        {/* Auto-outreach */}
        <section id="auto-outreach" className="scroll-mt-5 bg-white border border-[#E3E2E0] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FFF4E5] rounded-lg flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-[#B35900]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#191919]">Auto-Outreach</h2>
                <p className="text-xs text-[#6B6B6B]">Automatic first-touch outreach to new leads</p>
              </div>
            </div>
            <Toggle checked={outreachEnabled} onChange={setOutreachEnabled} testid="outreach-enabled-toggle" />
          </div>

          {outreachEnabled && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#191919] mb-1">Message template</label>
                <textarea
                  value={outreachTemplate}
                  onChange={(e) => setOutreachTemplate(e.target.value)}
                  placeholder="Hi {{name}}, I'd love to connect..."
                  rows={4}
                  className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm resize-none"
                  data-testid="outreach-template"
                />
                <p className="text-xs text-[#6B6B6B] mt-1">Use {'{{name}}'} to insert the lead's name</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1">Delay (minutes)</label>
                  <input
                    type="number"
                    value={outreachDelay}
                    onChange={(e) => setOutreachDelay(parseInt(e.target.value) || 60)}
                    min={1}
                    className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="outreach-delay"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#191919] mb-1">Max per day</label>
                  <input
                    type="number"
                    value={outreachMaxPerDay}
                    onChange={(e) => setOutreachMaxPerDay(parseInt(e.target.value) || 50)}
                    min={1}
                    max={100}
                    className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm"
                    data-testid="outreach-max"
                  />
                </div>
              </div>
              <button
                onClick={handleSaveOutreach}
                disabled={loading}
                className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                data-testid="save-outreach-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          )}
        </section>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, testid, disabled = false }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        onChange(!checked);
      }}
      className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${
        checked ? 'bg-[#0A0A0A]' : 'bg-[#E3E2E0]'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      data-testid={testid}
      aria-checked={checked}
      aria-disabled={disabled}
      role="switch"
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function CheckRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-[#0A0A0A]"
      />
      <span className="text-sm text-[#191919]">{label}</span>
    </label>
  );
}

// Keep Key icon import usable even though not directly referenced now.
void Key;
